/**
 * Humanizes long, viem-formatted transaction errors into short, user-facing
 * copy for the manage-position views.
 *
 * Raw viem errors are structured like:
 *
 *   Details: User rejected the request.
 *   Request Arguments: ...
 *   Contract Call: ...
 *   Docs: ...
 *   Version: viem@2.21.37
 *
 * We throw away the `Request Arguments` / `Contract Call` / `Docs` / `Version`
 * sections and distil the remaining text down to a friendly sentence. Anything
 * we don't recognise is returned as the first meaningful line of the error so
 * we never surface a wall of calldata to the end user.
 *
 * Wallet-cancellation errors deliberately return `null` — the user already
 * knows they hit "reject", so telling them "you cancelled the transaction" is
 * noise that every web3 app gets wrong. Callers should treat `null` as
 * "suppress the error banner". This mirrors the swap flow's silent-error
 * behaviour in `swap-execution-store.setError`.
 */

// Pattern used to detect deliberate wallet cancellations. Kept generous so it
// covers viem, wagmi, ethers, and raw wallet-provider strings.
export const USER_REJECTION_PATTERN =
  /user rejected|user denied|rejected the request|action_rejected|request rejected/i

type KnownErrorMatcher = {
  test: (raw: string) => boolean
  message: string
}

// Order matters - first match wins. The patterns are intentionally generous so
// they catch viem, wagmi, and bare wallet-provider strings.
const KNOWN_ERRORS: ReadonlyArray<KnownErrorMatcher> = [
  {
    test: (raw) => /insufficient funds/i.test(raw),
    message: "Insufficient balance to cover this transaction.",
  },
  {
    test: (raw) => /insufficient allowance|erc20: transfer amount exceeds allowance/i.test(raw),
    message: "Token approval is too low. Approve the token and try again.",
  },
  {
    test: (raw) => /nonce too low/i.test(raw),
    message: "Wallet nonce is out of sync. Refresh your wallet and try again.",
  },
  {
    test: (raw) => /replacement (transaction underpriced|fee too low)/i.test(raw),
    message: "Replacement gas price is too low. Speed up or cancel the pending tx in your wallet.",
  },
  {
    test: (raw) => /slippage|price slipped|too little received|too much requested/i.test(raw),
    message: "Price moved beyond your slippage tolerance. Try again or raise slippage.",
  },
  {
    test: (raw) => /deadline|transaction (timed out|expired)/i.test(raw),
    message: "Transaction deadline expired before it confirmed. Please try again.",
  },
  {
    test: (raw) => /chain mismatch|unsupported chain|wrong network/i.test(raw),
    message: "Your wallet is on the wrong network. Switch chains and try again.",
  },
  {
    test: (raw) => /execution reverted|contract function "multicall" reverted/i.test(raw),
    message: "The transaction reverted on-chain. Refresh the position and try again.",
  },
  {
    test: (raw) => /rpc|network request failed|failed to fetch/i.test(raw),
    message: "Network error reaching the RPC. Check your connection and try again.",
  },
]

/**
 * Strip the verbose sections viem appends to its error messages and return
 * the first meaningful line. Used as a fallback when no known pattern matches.
 */
const stripViemNoise = (raw: string): string => {
  const trimmed = raw.trim()
  if (!trimmed) return "Something went wrong. Please try again."

  // Viem concatenates sections with double newlines. Drop everything after the
  // first `Request Arguments:` / `Contract Call:` / `Docs:` / `Version:` marker.
  const cutoffRegex = /\n\s*(Request Arguments|Contract Call|Docs|Version|Details):/i
  const [head] = trimmed.split(cutoffRegex)
  const firstLine = (head ?? trimmed).split("\n")[0]?.trim() ?? trimmed
  const clean = firstLine.replace(/^Error:\s*/i, "")
  if (!clean) return "Something went wrong. Please try again."

  // Viem's top-level message is usually a short sentence, but some providers
  // shove calldata / selectors in there. Guard against an unreadable blob.
  if (clean.length > 140) return "Something went wrong. Please try again."
  return clean
}

export const humanizeTransactionError = (error: unknown): string | null => {
  if (!error) return "Something went wrong. Please try again."

  const raw =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? error.message
        : ""

  if (!raw) return "Something went wrong. Please try again."

  // User deliberately cancelled in their wallet — stay silent. Every web3 app
  // surfaces "you cancelled" and every user already knows they cancelled, so
  // the most respectful thing we can do is show nothing at all.
  if (USER_REJECTION_PATTERN.test(raw)) return null

  for (const matcher of KNOWN_ERRORS) {
    if (matcher.test(raw)) return matcher.message
  }

  return stripViemNoise(raw)
}
